import { siteApiSlice } from "../../app/siteApiSlice";
import { UserRole } from "../../app/userSlice";
import NamedEntity from "../../interfaces/NamedEntity";

export interface CourseSelectViewModel {
	userId: number;
	role: UserRole;
	showAddCourseLink: boolean;
	onlyShowActive: boolean;
	showEnrollmentSection: boolean;
	showSchoolColumn: boolean;
	alerts: Record<number, string>;
	clearDismissedAlerts: boolean;
}

export interface CourseRegistrationsViewModel {
	courses: CourseRegistrationViewModel[];
}

export interface CourseRegistrationViewModel {
	courseId: number;
	createDate: string;
	status: string;
	courseReferralCode: string;
	courseGrade: string;
	closeDate: string;
	courseName: string;
	isFavorite: boolean;
	school: string;
}

export interface ChangeFavoriteModel {
	userCourseId: number;
	favoriteState: boolean;
}

export const EmptyCourseRegistrationsViewModel: CourseRegistrationsViewModel = {
	courses: [],
};

export interface CreateCourseViewModel {
	districtCode: string;
	currentSchools: NamedEntity[];
	isDateRangeRequired: boolean;
	showScoringLevelExplanation: boolean;
}

export interface CreateCourseEditModel {
	name: string;
	school: NamedEntity;
	gradeId: number;
	beginDate?: Date;
	endDate?: Date;
}

export const coursesApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		getSelectCoursePage: build.query<CourseSelectViewModel, void>({
			query: () => `/SignIn/SelectCourse`,
			providesTags: ["Alerts"],
		}),
		getCourseEnrollments: build.query<CourseRegistrationsViewModel, boolean>({
			query: (getAll) => ({
				url: `/SignIn/GetCourseEnrollments`,
				params: { getAll },
			}),
			providesTags: ["CourseRegistrations"],
		}),
		changeFavorite: build.mutation<void, ChangeFavoriteModel>({
			query: (payload) => ({
				url: `/SignIn/UpdateFavoriteCourse`,
				method: "POST",
				body: payload,
			}),
		}),
		makeCourseSelection: build.mutation<boolean, number>({
			query: (courseId) => ({
				url: `/SignIn/MakeCourseSelection/${courseId}`,
				method: "GET",
			}),
			invalidatesTags: ["CurrentCourse"],
		}),
		registerForCourse: build.mutation<void, string>({
			query: (courseCode) => ({
				url: `/Registration/RegisterForCourse`,
				params: { courseCode },
				method: "GET",
			}),
			invalidatesTags: ["CourseRegistrations"],
		}),
		getCreateCourse: build.query<CreateCourseViewModel, string | undefined>({
			query: (districtCode?) => ({
				url: `/Registration/CreateCourse`,
				params: districtCode ? { districtCode } : undefined,
				method: "GET",
			}),
			providesTags: ["CourseRegistrations"],
		}),
		createCourse: build.mutation<string, CreateCourseEditModel>({
			query: (payload) => ({
				url: `/Registration/CreateCourseForSpa`,
				body: payload,
				method: "POST",
			}),
			invalidatesTags: ["CourseRegistrations"],
		}),
	}),
});

export const {
	useGetSelectCoursePageQuery,
	useGetCourseEnrollmentsQuery,
	useChangeFavoriteMutation,
	useMakeCourseSelectionMutation,
	useRegisterForCourseMutation,
	useGetCreateCourseQuery,
	useCreateCourseMutation,
} = coursesApiSlice;
